import { AxiosError } from 'axios';
import { Auth, IAuthResponse, ISessionObject, ITokenObject } from '../Auth';
import { AuthProvider } from './AuthProvider';

export type ApiKeyAuthParameter = {
  apiKey: string;
};
export class ApiKeyLogin extends AuthProvider<ApiKeyAuthParameter> {
  private apiKey = '';
  constructor(auth: Auth) {
    super(auth);
  }

  public login(credential: ApiKeyAuthParameter): Promise<ITokenObject> {
    this.validateConfig();
    if (!credential.apiKey) {
      return Promise.reject('MISSING APIKEY');
    }
    this.apiKey = credential.apiKey;
    return this.requestToken();
  }

  /**
   * Get session details from localstorage
   *
   * ```js example
   * dory.Auth.loginProvider.getSession()
   *   .catch(function (error) {
   *     console.error(error); // catch api errors
   *   })
   *   .then(function (result) {
   *     console.log(result);
   *     // > { exp, msLeft, sid, sid_core, sid_ngp, user }
   *   });
   * ```
   */
  public getSession(): Promise<ISessionObject> {
    return Promise.resolve<ISessionObject>({
      exp: Date.now() + 60000,
      isSSO: false,
      msLeft: 60000,
      initialDuration: 60000,
      sid: '', // cookie
      sid_core: null,
      sid_ngp: '', // cookie
      user: '',
    });
  }

  // override
  public requestToken(): Promise<ITokenObject> {
    if (this.tokenPromise !== null) {
      // Return the Promise for the already-requested token to avoid multiple token requests
      return this.tokenPromise;
    }

    const header = { 'X-API-KEY': this.apiKey };

    this.tokenPromise = this.auth
      .request({
        url: this.auth.Config.auth.urlToken,
        method: 'post',
        headers: header,
      })
      .then(async (response: IAuthResponse) => {
        if (!this.validateTokenResponse(response)) {
          return this.setToken(null).then(() => {
            throw new Error('MISSING_TOKEN_IN RESPONSE');
          });
        }
        return this.setToken(response.data).then(() => this.getToken());
      })
      .catch((error: AxiosError) => {
        return Promise.reject(error);
      })
      .finally(() => {
        this.tokenPromise = null;
      });
    return this.tokenPromise;
  }
}
